import { useEffect, useState } from "react";

import FamilleGridRow from "./FamilleGridRow";
import { patientsValidStates } from "../filterTransforms/patientsValidStates";
import { useSelectedPatientIds } from "../../main/state/globalState";
import { DDNSchema, E164Schema, ExistingPatient, Patients } from "cadendar-shared";
import { FamilleGridKeys } from "../../patients/validators/individualValidators.ts";
import {Button} from "@/components/ui/button";
import styles from "./FamilleGridPanel.module.css";
import { FaPlus } from "react-icons/fa";
import FamilleExistingPatientAdder from "./FamilleExistingPatientAdder.tsx";
import PageChooser from "../../ficheAppel/components/PageChooser.tsx";
import _ from "underts";
import { formaters } from "vincent-utils";
import useSelectedPatients from "../../patients/hooks/useSelectedPatients.ts";
import { trpc } from "../../main/components/trpc.ts";
import { produce } from "immer";
import useTempPatient from "../../ficheAppel/hooks/useTempPatient.ts";
import z from "zod/v4";
import { skipToken } from "@tanstack/react-query";
import { applyFamilyRank } from "cadendar-shared/patients/applyFamilyRank";
import { nameMatch } from "cadendar-shared/nameMatch";
import { convertLocalNewPatientToNewPatient } from "../filterTransforms/convertLocalNewPatientToNewPatient.ts";

const requiredFields = ["name", "forename", "birthdate", "famille_id"];

export type LocalNewPatient = Omit<Patients, "id" | "owner" | "patient_portal_key">;

type PatientDisplay = Omit<Patients, "patient_portal_key">;

const memberIsReadyToSave = (membre: PatientDisplay | LocalNewPatient) => {
  const allMembersValid = Object.keys(membre).every((key) => {
    switch (key) {
      case "name":
        return membre.name.length > 0;
      case "forename":
        return membre.forename.length > 0;
      case "birthdate":
        return DDNSchema.safeParse(membre.birthdate).success;
      case "portablee164":
        return (
          !membre.portablee164 ||
          E164Schema.safeParse(membre.portablee164).success
        ); //empty string is ok
      // case "famille_id":
      //   return membre.famille_id.length > 0;
      case "email":
        return (
          !membre.email || z.string().email().safeParse(membre.email).success
        );
      default:
        return true;
    }
  });
  const presentKeys = Object.keys(membre);
  return (
    requiredFields.every((field) => presentKeys.includes(field)) &&
    allMembersValid
  );
};

const familyIsReadyToSave = (membres: (PatientDisplay | LocalNewPatient)[]) => {
  const everyMemberOk = membres.every(memberIsReadyToSave);
  // const familleIds = membres.map((m) => m.famille_id);
  // const uniqueFamilleIds = new Set(familleIds);
  // const familleIdIsUnique = uniqueFamilleIds.size === 1;
  // console.log("familleIdIsUnique", familleIdIsUnique);
  // console.log("uniqueFamilleIds", uniqueFamilleIds);
  // console.log("familleIds", familleIds);
  // console.log("everyMemberOk", everyMemberOk);
  return everyMemberOk;
};

// const onSetFamilleId = (
//   membres: (LocalNewPatient | LocalExistingPatient)[],
//   forceId?: string
// ) => {
//   function applyFamilleId(
//     membres: (LocalNewPatient | LocalExistingPatient)[],
//     famille_id: string
//   ) {
//     return membres.map((m) => {
//       return { ...m, famille_id };
//     });
//   }
//   if (forceId) {
//     console.log("forceId", forceId);
//     return applyFamilleId(membres, forceId);
//   }
//   const familleIdFromMembres = _.uniq(membres.map((p) => p.famille_id));
//   if (familleIdFromMembres.length === 1) {
//     const famille_id = _.first(familleIdFromMembres) as string;
//     return applyFamilleId(membres, famille_id);
//   }
//   if (familleIdFromMembres.length === 0) {
//     const famille_id = uuidv4();
//     return applyFamilleId(membres, famille_id);
//   }
//   throw new Error("multiple famille_id in membres");
// };

const patientDdnCompare = (
  a: { birthdate: string },
  b: { birthdate: string }
) => {
  if (!a.birthdate) {
    return 1;
  }
  if (!b.birthdate) {
    return -1;
  }
  const parsedA = formaters.parseDateString(a.birthdate);
  if (!parsedA) {
    return 1;
  }
  const parsedB = formaters.parseDateString(b.birthdate);
  if (!parsedB) {
    return -1;
  }
  return parsedA.getTime() - parsedB.getTime();
};

const hasId = <T extends Record<string, any>>(p: T): p is T & { id: string } =>
  "id" in p;

// const isNewPatient = (p: unknown): p is NewPatient =>
//   newPatientSchema.safeParse(p).success;

// const isExistingPatient = (p: unknown): p is ExistingPatient =>
//   existingPatientSchema.safeParse(p).success;

  const sortPatients = (patients: (PatientDisplay | LocalNewPatient)[]) => {
  const patientsWithIds = patients.filter(hasId);
  const patientsWithoutIds = patients.filter((p) => !hasId(p));
  patientsWithIds.sort(patientDdnCompare);
  return [...patientsWithIds, ...patientsWithoutIds];
};

interface FamilleGridPanelProps {
  isFicheAppelMode: boolean;
  onPrevPage?: () => void;
  onNextPage?: () => void;
  onClose: () => void;
}

function convertToLocalPatient<P extends ExistingPatient>(patient: P) {
  return {
    name: patient.name || "",
    forename: patient.forename || "",
    portablee164: patient.portablee164
      ? formaters.formatTelNumberForDisplay(patient.portablee164)
      : "",
    birthdate: patient.birthdate || "",
    email: patient.email || "",
    famille_id: patient.famille_id || null,
    patient_flags: null,
    adr_id: null,
    seen: null,
  };
}

// export function convertLocalExistingPatientToExistingPatient(
//   patient: LocalExistingPatient
// ) {
//   const e164 = E164Schema.safeParse(patient.portablee164);
//   return {
//     id: patient.id,
//     name: patient.name,
//     forename: patient.forename,
//     portableE164: e164.success ? e164.data : null,
//     ddn: DDNSchema.parse(patient.birthdate),
//     email: patient.email,
//     famille_id: patient.famille_id || null,
//     seen: patient.seen || null,
//     former_patient: patient.former_patient || null,
//     raf: patient.raf || null,
//     comments: patient.comments || "",
//     adr_flag: patient.adr_flag || null,
//     adr_other_text: patient.adr_other_text || null,
//     patient_flags: patient.patient_flags || null,
//     adr_id: patient.adr_id || null,
//   };
// }

// function convertToExistingPatient(patient: LocalExistingPatient) {
//   const e164 = E164Schema.safeParse(patient.portablee164);
//   return {
//     id: patient.id,
//     name: patient.name,
//     forename: patient.forename,
//     portableE164: e164.success ? e164.data : null,
//     birthdate: DDNSchema.parse(patient.birthdate),
//     email: patient.email || null,
//     famille_id: patient.famille_id || null,
//     seen: patient.seen || null,
//     former_patient: patient.former_patient || null,
//     raf: patient.raf || null,
//     comments: patient.comments || "",
//     adr_flag: patient.adr_flag || null,
//     adr_other_text: patient.adr_other_text || null,
//     patient_flags: patient.patient_flags || null,
//   };
// }

const FamilleGridPanel = (props: FamilleGridPanelProps) => {
  const [membres, setMembres] = useState<(PatientDisplay | LocalNewPatient)[]>([]);
  const selectedPatients = useSelectedPatients();
  const selectedPatient = _.first(selectedPatients);
  const [isAddingExistingPatient, setIsAddingExistingPatient] = useState(false);
  const [selectedIndexes, setSelectedIndexes] = useState<Set<number>>(new Set());
  // const [, setFamilleGridOpenState] = useAtom(FamilleGridOpenState);

  const [, { selectPatientIds }] = useSelectedPatientIds();

  const togglePatientSelection = (idx: number) => {
    setSelectedIndexes((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  const selectPatientAtIndex = (idx: number) => {
    setSelectedIndexes((prev) => {
      const next = new Set(prev);
      next.add(idx);
      return next;
    });
  };

  const { tempPatient, adresseurPatient, selectedFamilleAdressedBy } =
    useTempPatient();

  useEffect(() => {
    if (membres.length === 0 && selectedPatient) {
      addPatient(selectedPatient);
    }
  }, [JSON.stringify(selectedPatient)]);

  useEffect(() => {
    if (selectedFamilleAdressedBy && adresseurPatient) {
      addPatient(adresseurPatient).then(() => {
        addPatient(tempPatient);
      });
    }
  }, [JSON.stringify(selectedFamilleAdressedBy)]);
  console.log("tempPatient", tempPatient);

  const _onPatientsFetched = (patients: ExistingPatient[]) => {
    setMembres((membres) => {
      const membresToAdd = patients.filter(
        (patient) => !membres.find((p) => "id" in p && p.id === patient.id)
      );
      return sortPatients([...membres, ...membresToAdd]);
    });
  };
  const familleIds = membres.map((m) => m.famille_id);
  const uniqueFamilleIds = [...new Set(familleIds)];
  console.log("uniqueFamilleIds", uniqueFamilleIds);
  const familleId = _.first(uniqueFamilleIds);

  const patientsToLoadResult = trpc.patient.findByFamilleId.useQuery(
    familleId ? { familleId: familleId } : skipToken
  );

  useEffect(() => {
    patientsToLoadResult.data && _onPatientsFetched(patientsToLoadResult.data);
  }, [JSON.stringify(patientsToLoadResult.data)]);

  // useEffect(() => {
  //   setFamilleGridOpenState(true);
  // }, []);

  const stateIsReadyToSave = familyIsReadyToSave(membres);
  console.log("membres", membres);
  console.log("stateIsReadyToSave", stateIsReadyToSave);
  console.log("disabled", !stateIsReadyToSave);
  const addNewPatient = () => {
    //les nouveaux patients n'ont pas encore d'id car pas enregistrés dans la base
    //utile de les laisser comme ça pour qu'ils soient classés correctement
    setMembres((membres) => {
      const newMembres = [
        ...membres,
        {
          name: "",
          forename: "",
          portablee164: null,
          birthdate: "",
          email: null,
          email_consent: null,
          sms_consent: null,
          patient_flags: null,
          adr_id: null,
          adr_flag: null,
          adr_other_text: null,
          comments: "",
          seen: null,
          former_patient: false,
          raf: null,
          famille_id: null,
          logos_file_number: null,
          genre: null,
          address: null,
          address_complement: null,
          city: null,
          postal_code: null,
          insee_number: null,
          familly_rank: null,
          maiden_name: null,
        },
      ];
      const newIdx = newMembres.length - 1;
      setSelectedIndexes((prev) => {
        const next = new Set(prev);
        next.add(newIdx);
        return next;
      });
      return newMembres;
    });
  };

  const updatePatient = <ITEM extends FamilleGridKeys>(
    idx: number,
    item: ITEM,
    value: LocalNewPatient[ITEM]
  ) => {
    setMembres((membres) =>
      produce(membres, (draft) => {
        draft[idx][item] = value;
      })
    );
  };

  const copyDown = <ITEM extends FamilleGridKeys>(idx: number, item: ITEM) => {
    setMembres((membres) =>
      produce(membres, (draft) => {
        const pat = draft[idx];
        const value = pat[item];
        draft.forEach((_, i) => {
          if (i > idx && !draft[i][item]) {
            draft[i][item] = value;
          }
        });
      })
    );
  };

  const addPatient = async (patient: PatientDisplay | LocalNewPatient) => {
    console.log("addPatient", patient);
    setMembres((membres) => {
      if ("id" in patient) {
        //il est déjà dans les membres
        if (membres.find((p) => hasId(p) && p.id === patient.id)) {
          console.warn("patient already in famille");
          return membres;
        }
      }
      const addedPatientDDN = patient.birthdate;
      if (
        membres.find(
          (p) =>
            nameMatch(p.forename, patient.forename) &&
            p.birthdate === addedPatientDDN
        )
      ) {
        console.warn("patient with same ddn and forename already in famille");
        return membres;
      }
      let newMembres = [...membres, patient];
      // if (patient.famille_id) {
      //   newMembres = onSetFamilleId(newMembres, patient.famille_id); //si famille_id est undefined, ça marche aussi
      // }

      newMembres = sortPatients(newMembres);
      newMembres = applyFamilyRank(newMembres, new Date());
      return newMembres;
    });
  };

  // const ensureId = (membres: (LocalExistingPatient | LocalNewPatient)[]) => {
  //   const famille_id =
  //     membres.find((m) => !!m.famille_id)?.famille_id || uuidv4();
  //   return onSetFamilleId(membres, famille_id);
  // };

  // const removeId = (membre: LocalExistingPatient | LocalNewPatient) => {
  //   return { ...membre, familleId: "" };
  // };

  const utils = trpc.useUtils();
  const removeFamilleIdMutation = trpc.patient.removeFamilleId.useMutation({
    onSuccess: () => {
      utils.patient.invalidate();
    },
  });
  const removePatientFromFamille = (idx: number) => {
    const patientToRemove = membres[idx];
    setMembres((membres) =>
      produce(membres, (draft) => {
        if (idx < draft.length) {
          draft.splice(idx, 1);
        } else {
          throw new Error(
            "trying to remove a patient at an index out of range"
          );
        }
      })
    );

    if ("id" in patientToRemove) {
      removeFamilleIdMutation.mutate(patientToRemove.id);
    }
  };

  // const saveEditePatientMutation = trpc.patient.saveEdited.useMutation({
  //   onSuccess: () => {
  //     utils.patient.invalidate();
  //     props.onClose();
  //   },
  // });
  const updatePatientMutation = trpc.patient.update.useMutation({
    onSuccess: () => {
      utils.patient.invalidate();
      props.onClose();
    },
  });
  // const saveNewPatientMutation = trpc.patient.create.useMutation({
  //   onSuccess: (id) => {
  //     utils.patient.invalidate();
  //     selectPatientId(id);
  //     props.onClose();
  //   },
  // });
  const commitFamilyMutation = trpc.patient.commitFamillyPatients.useMutation({
    onSuccess: (result) => {
      utils.patient.invalidate();
      props.onClose();
      const commitResult = result as { createdIds: string[]; existingIds: string[] };
      const newPatientsInMembres = membres.filter((m) => !hasId(m));
      const selectedIds = Array.from(selectedIndexes)
        .map((idx) => {
          const patient = membres[idx];
          if (hasId(patient)) {
            return patient.id;
          }
          const newPatientIdx = newPatientsInMembres.indexOf(patient);
          return commitResult.createdIds[newPatientIdx];
        })
        .filter((id): id is string => !!id);
      selectPatientIds(selectedIds);
    },
  });
  const savePatients = async () => {
    if (membres.length === 0) {
      return false;
    }
    const existingPatients = membres.filter(
      (p) => "id" in p
    );
    const newPatients = membres.filter((p) => !("id" in p));
    console.log("existingPatients", existingPatients);
    console.log("newPatients", newPatients);
    // if (
    //   existingPatients.find((p) => !p.famille_id) &&
    //   existingPatients.length !== 1
    // ) {
    //   throw new Error(
    //     "famille_id is undefined while saving patient " +
    //       JSON.stringify(existingPatients, null, 2)
    //   );
    // }
    // if (newPatients.find((p) => !p.famille_id) && newPatients.length !== 1) {
    //   throw new Error(
    //     "famille_id is undefined while saving patient " +
    //       JSON.stringify(newPatients, null, 2)
    //   );
    // }
    // existingPatients
    //   .map(convertToExistingPatient)
    //   .forEach((patient) =>
    //     updatePatientMutation.mutate({ id: patient.id, props: patient })
    //   );
    // newPatients.map(convertLocalNewPatientToNewPatient).forEach((patient) => {
    //   saveNewPatientMutation.mutate(patient);
    // });
    commitFamilyMutation.mutate([...existingPatients, ...newPatients]);
  };

  const onFamilleEditCancel = () => {
    props.onClose();
  };

  const onFamilleSave = async () => {
    savePatients();
  };
  const onAddPatient = (patient: ExistingPatient) => {
    addPatient(patient);
    setTimeout(() => {
      const idx = membres.findIndex((m) => hasId(m) && m.id === patient.id);
      if (idx !== -1) {
        selectPatientAtIndex(idx);
      }
    }, 0);
  };

  const onCopyDown = (idx: number) => (carac: FamilleGridKeys) => {
    copyDown(idx, carac);
  };
  const onRemovePatientFromFamille = (idx: number) => () => {
    removePatientFromFamille(idx);
  };
  const validArr = patientsValidStates(membres);
  const mainValid = validArr.every((valid) => valid);

  const handleNextPage = () => {
    savePatients().then(() => {
      props.onNextPage ? props.onNextPage() : null;
    });
  };
  const rowComps = membres.map((membre, idx) => (
    <FamilleGridRow
      {...membre}
      onMembreChange={(carac, value) => updatePatient(idx, carac, value)}
      isValid={validArr[idx]}
      familyRank={idx}
      key={("id" in membre && membre.id) || idx}
      onCopyDown={onCopyDown(idx)}
      onRemoveMembre={onRemovePatientFromFamille(idx)}
      isSelected={selectedIndexes.has(idx)}
      onToggle={() => togglePatientSelection(idx)}
    />
  ));
  const rowComposWithButton = [
    ...rowComps,
    <tr key="newPatientButtonRow">
      <td>
        <Button
          onClick={addNewPatient}
          id="addLineFamilleGrid"
          title="Créer nouveau patient"
          data-testid="addLineFamilleGrid"
          size="icon-sm"
          variant="secondary"
          className={styles.col3}
        >
          <FaPlus />
        </Button>
      </td>
      <td></td>
      <td></td>
      <td></td>
      <td></td>
      <td></td>
      <td></td>
    </tr>,
  ];
  const existingMembresComp = isAddingExistingPatient ? (
    <FamilleExistingPatientAdder
      onAddPatient={onAddPatient}
      initialNom={membres[0]?.name ?? ""}
    />
  ) : (
    <span></span>
  );
  console.log("membres", JSON.stringify(membres, null, 2));

  return (
    <div>
      <div className={styles.verticalSpacer}>
        <table className="" id="familleGrid">
          <thead>
            <tr className={styles.header}>
              <th></th>
              <th>Nom</th>
              <th>forename</th>
              <th>DDN</th>
              <th>Portable</th>
              <th>Email</th>
              <th>Rôle</th>
            </tr>
          </thead>
          <tbody>{rowComposWithButton}</tbody>
        </table>
        <div className={styles.verticalSpacer}>
          <div>
            <Button
              onClick={() => setIsAddingExistingPatient(true)}
              id={"addExistingPatientToFamille"}
              variant="secondary"
              size="sm"
            >
              Ajouter Patient Existant
            </Button>
          </div>
          {existingMembresComp}
          {props.isFicheAppelMode && props.onPrevPage ? (
            <PageChooser
              prevPageEnabled={true}
              nextPageEnabled={stateIsReadyToSave}
              onPrevPage={props.onPrevPage}
              onNextPage={handleNextPage}
              prevPageText="Précédent"
              nextPageText="Suivant"
            />
          ) : (
            <div className={styles.horizontalSpacer}>
              <Button
                onClick={onFamilleSave}
                id={"familleGridSaveInDb"}
                disabled={!stateIsReadyToSave}
              >
                Enregistrer
              </Button>
              <Button
                onClick={onFamilleEditCancel}
                id={"familleGridClearSession"}
                variant="secondary"
              >
                Annuler modifications
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FamilleGridPanel;
